import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });

  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate form submission
    console.log('Form submitted:', formData);
    setFormStatus('success');
    setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
    setTimeout(() => setFormStatus('idle'), 5000);
  };

  return (
    <main className="min-h-screen bg-white text-zinc-900 overflow-x-hidden">
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-80 flex items-center justify-center px-6 py-32 mt-16 bg-gradient-to-br from-blue-600 to-cyan-500">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Get In Touch
          </motion.h1>
          <motion.p
            className="text-xl text-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Schedule your free consultation or request more information about our services
          </motion.p>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8">
            <motion.div
              className="bg-white p-8 rounded-xl border border-gray-200 hover:border-blue-300 transition-all duration-300 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="inline-block p-4 bg-blue-100 rounded-lg mb-4">
                <Phone className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-zinc-900 mb-2">Phone</h3>
              <p className="text-zinc-600 mb-2">+972 54 4707685</p>
              <p className="text-sm text-zinc-500">Available 24/7</p>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-xl border border-gray-200 hover:border-blue-300 transition-all duration-300 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="inline-block p-4 bg-green-100 rounded-lg mb-4">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-zinc-900 mb-2">Email</h3>
              <p className="text-zinc-600 mb-2">info@talinvestments.net</p>
              <p className="text-sm text-zinc-500">Response within 2 hours</p>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-xl border border-gray-200 hover:border-blue-300 transition-all duration-300 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="inline-block p-4 bg-orange-100 rounded-lg mb-4">
                <MapPin className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-bold text-zinc-900 mb-2">Location</h3>
              <p className="text-zinc-600 mb-2">Herzelia, Israel</p>
              <p className="text-sm text-zinc-500">Technology Hub Central</p>
            </motion.div>

            <motion.div
              className="bg-white p-8 rounded-xl border border-gray-200 hover:border-blue-300 transition-all duration-300 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="inline-block p-4 bg-purple-100 rounded-lg mb-4">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-zinc-900 mb-2">Business Hours</h3>
              <p className="text-zinc-600 mb-2">Sunday - Thursday</p>
              <p className="text-sm text-zinc-500">8:00 AM - 6:00 PM</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-zinc-900 mb-8">Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-zinc-900 mb-2">Your Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                    placeholder="John Doe"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-zinc-900 mb-2">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-zinc-900 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                      placeholder="+972 (0)9 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-zinc-900 mb-2">Company Name</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                    placeholder="Your Company"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-zinc-900 mb-2">Service Interest</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200"
                  >
                    <option value="">Select a service...</option>
                    <option value="photography">Aerial Photography & Videography</option>
                    <option value="mapping">Mapping & Surveying</option>
                    <option value="inspection">Infrastructure Inspection</option>
                    <option value="thermal">Thermal Imaging</option>
                    <option value="rescue">Search & Rescue Support</option>
                    <option value="custom">Custom Solutions</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-zinc-900 mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-200 resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  Send Message
                </button>

                {formStatus === 'success' && (
                  <motion.div
                    className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    ✓ Thank you! We'll be in touch soon.
                  </motion.div>
                )}
              </form>
            </motion.div>

            {/* Info & Map */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-4">Herzelia, Israel</h3>
                <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl h-80 flex items-center justify-center border-2 border-blue-200">
                  <div className="text-center">
                    <MapPin className="w-16 h-16 text-blue-400 mx-auto mb-4 opacity-50" />
                    <p className="text-zinc-600 font-semibold">Sightline UAV Headquarters</p>
                    <p className="text-zinc-500 text-sm mt-2">Herzelia Technology Park</p>
                    <p className="text-zinc-500 text-sm">Herzelia, Israel</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-8 rounded-xl border border-blue-200">
                <h3 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                  Quick Response Guaranteed
                </h3>
                <p className="text-zinc-700 mb-4">
                  Our team is standing by to assist you. Whether you have questions about our services or are ready to discuss your project, we're here to help.
                </p>
                <ul className="space-y-2 text-zinc-600">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                    Response within 2 business hours
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                    24/7 emergency support available
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                    Free consultation with no obligations
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-4">Frequently Asked Questions</h2>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: 'How long does a typical project take?',
                a: 'Project duration varies based on scope. Small projects may take 1-2 days, while complex operations can span weeks. We provide detailed timelines during consultation.'
              },
              {
                q: 'Do you operate in bad weather?',
                a: 'Safety is paramount. We use real-time weather monitoring and operate within strict safety parameters. Most operations require calm weather conditions.'
              },
              {
                q: 'What areas do you serve?',
                a: 'Based in Herzelia, we serve all of Israel and can deploy internationally. We handle emergency requests with rapid response times.'
              },
              {
                q: 'Are you licensed and insured?',
                a: 'Yes, we maintain all required Israeli Civil Aviation Authority licenses and comprehensive insurance coverage for all operations.'
              },
              {
                q: 'What is your typical project cost?',
                a: 'Pricing depends on service type, duration, and complexity. We offer competitive rates and flexible packages tailored to your budget.'
              },
              {
                q: 'Can you provide same-day service?',
                a: 'For certain services, yes. Contact us immediately for urgent requests and we\'ll assess availability.'
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg border border-gray-200 hover:border-blue-300 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <h3 className="font-bold text-zinc-900 mb-2">{item.q}</h3>
                <p className="text-zinc-600">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};
